import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MessagesSquare, ChevronLeft } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import usePersistentState from "../../hooks/usePersistentState";
import { getConversation, getAILog } from "../../services/aiChatService";
import { useSidebar } from "../../providers/SidebarProvider";
import HelpdeskSuiteLayout, {
  hasBlockingModifier,
  isTypingTarget,
} from "../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { formatPhone } from "../../layouts/helpdeskLayout/HelpdeskRail";
import useWriteToDriver from "../../components/helpdesk/useWriteToDriver";
import ConversationList, {
  CHANNEL_NAME,
  conversationDisplayName,
} from "../../components/helpdesk/ConversationList";
import PanelResizer from "../../components/helpdesk/PanelResizer";
import { clampWidths } from "../../components/helpdesk/layoutWidths";
import ConversationDetail from "../../components/helpdesk/ConversationDetail";
import ContactPanel from "../../components/helpdesk/ContactPanel";
import LinkWhatsAppHint from "../../components/helpdesk/LinkWhatsAppHint";
import RetiredAccountBanner from "../../components/helpdesk/RetiredAccountBanner";
import {
  MessageSearchResults,
  TicketLookupResult,
} from "../../components/helpdesk/InboxSearchResults";
import { cn } from "../../utils/cn";
import { useHelpdeskInbox } from "./inbox/useHelpdeskInbox";
import { RETIRED_KEY_PREFIX, searchResultConversation } from "./inbox/inboxModel";
import { isPinnable } from "../../services/helpdeskPinsService";

// Duración de la transición de los paneles; el desmontaje del panel de contacto
// espera este tiempo para que el contenido no desaparezca de golpe al cerrarlo.
const PANEL_TRANSITION_MS = 200;

// Anchos de las columnas laterales, en px enteros: los decide el usuario
// arrastrando los divisores, así que un clamp por vw dejó de tener sentido.
// CHAT_MIN NO es una columna del grid: es la invariante que impide que la
// conversación —lo que el agente quiere ver más grande— quede aplastada entre
// la lista y el contacto.
const LIST_MIN = 280;
const LIST_DEFAULT = 340;
const LIST_MAX = 480;
const CONTACT_MIN = 280;
const CONTACT_DEFAULT = 320;
const CONTACT_MAX = 420;
const CHAT_MIN = 420;
// Piso del chat para ACOPLAR la columna de contacto. Second visual pass: the
// conversation gets priority. With 380 a 1440px laptop (sidebar + rail open,
// grid ≈ 986px) docked the panel and left the chat at ~400px, so long customer
// messages wrapped into tall narrow bubbles. With 520 the panel docks from
// ~1600px; below that, clicking the contact block opens it as the overlay
// (same path as under 1280px). The persisted open/closed preference is not
// touched.
const CHAT_DOCK_MIN = 520;
const RESIZE_STEP = 16; // paso del teclado en los divisores
// Límites para clampWidths (compartida con la vista de atención e incidencias).
const WIDTH_LIMITS = {
  listMin: LIST_MIN,
  listMax: LIST_MAX,
  sideMin: CONTACT_MIN,
  sideMax: CONTACT_MAX,
  chatMin: CHAT_MIN,
};

// ── Etiquetas del Excel de la bandeja ───────────────────────────────────────
//
// El export lo leen operadores NO técnicos: estados y tipos van en español
// legible, nunca los valores crudos del backend. La columna Canal reutiliza
// CHANNEL_NAME de ConversationList (misma fuente que las filas en pantalla).
// Tope de conversaciones por export: cada una es un GET de detalle contra
// producción; 200 secuenciales ya son ~1-2 min en el peor caso.
const EXPORT_MAX_CONVERSATIONS = 200;

const EXPORT_STATUS_LABELS = {
  pending: "Pendiente",
  assigned: "Asignada",
  resolved: "Resuelta",
  ai: "Atendida por IA",
};

const EXPORT_ROLE_LABELS = {
  user: "Cliente",
  agent: "Agente",
  ai: "IA",
  system: "Sistema",
};

// content_type → tipo legible y marcador para la celda de texto cuando el
// mensaje es un adjunto (mismos tipos que MediaRenderer en ConversationDetail).
const EXPORT_MESSAGE_TYPES = {
  text: { label: "Texto", marker: "" },
  image: { label: "Imagen", marker: "[imagen]" },
  audio: { label: "Audio", marker: "[audio]" },
  voice_note: { label: "Nota de voz", marker: "[nota de voz]" },
  video: { label: "Video", marker: "[video]" },
  document: { label: "Documento", marker: "[documento]" },
  sticker: { label: "Sticker", marker: "[sticker]" },
  location: { label: "Ubicación", marker: "[ubicación]" },
};

// Las funciones puras de la bandeja viven en ./inbox/inboxModel. Se
// re-exportan desde la página porque otros módulos y tests las importan de acá.
export {
  RETIRED_KEY_PREFIX,
  inboxKeyToBaseFilters,
  inboxKeyToClientFilter,
} from "./inbox/inboxModel";

// ── HelpdeskPage ─────────────────────────────────────────────────────────────

export default function HelpdeskPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const isWideDesktop = !useIsMobile(1280);

  // Datos de la bandeja (filtros, lista, detalle, carpetas, cuentas, búsqueda
  // server-side y tiempo real). La página compone encima lo que es de layout o
  // de entrada por URL: pantallas de móvil, anchos, modo foco, atajos,
  // deep-link ?phone=, location.state y export a Excel.
  const {
    inboxKey,
    setInboxKey,
    selectInbox,
    retiredAccountId,
    activeFolderId,
    accountFilter,
    commerceFilter,
    setCommerceFilter,
    labelFilter,
    setLabelFilter,
    showAllNumbers,
    setShowAllNumbers,
    searchQuery,
    setSearchQuery,
    conversations,
    visibleConversations,
    listLoading,
    listError,
    aiLogsError,
    newIds,
    counters,
    refreshList: fetchList,
    selectedConversation: selectedConv,
    detailMessages,
    detailLoading,
    detailError,
    selectedChannelAccount,
    selectConversation,
    openConversation,
    clearSelection,
    applyConversationUpdate: handleConvUpdated,
    applyTakeover: handleAiTakeover,
    reloadConversation,
    userNames,
    getDisplayName,
    folders,
    foldersById,
    createFolder: handleCreateFolder,
    renameFolder: handleRenameFolder,
    deleteFolder: handleDeleteFolder,
    moveToFolder: handleMoveToFolder,
    // Fijados por usuario: el hook los expone solo si el endpoint responde.
    pins,
    pinsAvailable,
    togglePin,
    channelAccounts,
    retiredAccount,
    notifyAccountsChanged: handleAccountsChanged,
    trimmedQuery,
    showMessageSearch,
    messageSearchLoading,
    messageSearchError,
    visibleMessageResults,
    ticketQueryNumber,
    ticketLookupVisible,
    ticketLookupFresh,
    ticketLookupLoading,
    ticketResult,
    ticketLookupError,
    bothSearchesEmpty,
  } = useHelpdeskInbox({
    // Si entraste a /admin/helpdesk desde otra ruta del rail, recibimos la
    // sub-view en location.state. Si no, default "Sin asignar".
    initialInboxKey: () => {
      if (location.state?.railKey) return location.state.railKey;
      // Deep-link ?phone= (p.ej. desde el detalle de usuario): la conversación
      // buscada es de WhatsApp, así que se entra directo por ese tab.
      if (new URLSearchParams(location.search).get("phone")) return "channel.whatsapp";
      return "inbox.unassigned";
    },
  });

  // Número pendiente de abrir vía ?phone=. Se consume UNA vez, contra la
  // primera lista cargada; los refetch posteriores no lo re-disparan.
  const deepLinkPhoneRef = useRef(
    new URLSearchParams(location.search).get("phone")?.replace(/\D/g, "") || null
  );

  useEffect(() => {
    if (location.state?.railKey && location.state.railKey !== inboxKey) {
      setInboxKey(location.state.railKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [mobileView, setMobileView] = useState("list");
  // Tercera columna (contacto) fija en desktop ancho. Toggleable en wide para
  // que el agente pueda ocultarla si quiere todo el ancho del chat; la
  // preferencia sobrevive recargas y navegaciones.
  const [contactPanelOpen, setContactPanelOpen] = usePersistentState(
    "contactPanel.open",
    true
  );

  // ── Anchos de las columnas laterales (arrastrables) ───────────────────────
  // Persistidos en px. El ancho VIVO del arrastre no vive aquí: se escribe
  // directo en una CSS var del contenedor (ver setWidthVar) para no re-renderizar
  // el inbox entero ni escribir en localStorage a 60fps.
  const [listWidth, setListWidth] = usePersistentState(
    "layout.listWidth",
    LIST_DEFAULT
  );
  const [contactWidth, setContactWidth] = usePersistentState(
    "layout.contactWidth",
    CONTACT_DEFAULT
  );
  // Modo foco: comprime todo lo accesorio (sidebar global, rail, lista) para
  // dejarle a la conversación el ancho que sobra. No muta ninguna preferencia.
  const [focusMode, setFocusMode] = usePersistentState("layout.focusMode", false);
  // Snapshot de lo que el modo foco pisó fuera de esta vista (el sidebar global,
  // que es estado compartido con toda la app). Persistido para poder restaurarlo
  // aunque la pestaña se cierre a mitad de camino.
  const [focusPrev, setFocusPrev] = usePersistentState("layout.focusPrev", null);

  const [isResizing, setIsResizing] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const gridRef = useRef(null);
  const dragRef = useRef(null); // { panel: 'list'|'contact', width } durante el arrastre

  const listPanelRef = useRef(null);

  // ── Exportar a Excel (conversaciones filtradas + sus mensajes) ────────────
  // Para operadores no técnicos: .xlsx con encabezados en español y fechas
  // legibles, nada de CSV. xlsx (SheetJS) se importa DINÁMICAMENTE al hacer
  // clic: pesa demasiado para cargarlo en el bundle inicial por una acción
  // ocasional. Los mensajes se piden UNO POR UNO (secuencial, no en paralelo:
  // son hasta 200 GETs contra producción) con progreso en el propio botón.
  const [exportProgress, setExportProgress] = useState(null); // {current,total}|null

  const handleExportExcel = useCallback(async () => {
    if (visibleConversations.length === 0 || exportProgress) return;

    let toExport = visibleConversations;
    if (visibleConversations.length > EXPORT_MAX_CONVERSATIONS) {
      const proceed = window.confirm(
        t(
          "Hay {{total}} conversaciones en el filtro actual. Se exportan las primeras {{max}}. ¿Continuar?"
        )
          .replace("{{total}}", visibleConversations.length)
          .replace("{{max}}", EXPORT_MAX_CONVERSATIONS)
      );
      if (!proceed) return;
      toExport = visibleConversations.slice(0, EXPORT_MAX_CONVERSATIONS);
    }

    setExportProgress({ current: 0, total: toExport.length });
    try {
      const XLSX = await import("xlsx");
      const formatDate = (value) =>
        value ? dayjs(value).format("DD-MM-YYYY HH:mm") : "";

      // Misma fuente que las filas en pantalla (el canal interno no tiene
      // contacto y cae a "Equipo interno").
      const contactOf = (c) => conversationDisplayName(c, getDisplayName, t) || "—";

      // "Quién": el rol legible siempre; el nombre resuelto solo si el cache
      // de useUserNames ya lo tiene (getDisplayName devuelve el id crudo
      // cuando no, y un ObjectId en la celda no le dice nada a un operador).
      const whoOf = (m) => {
        const role = EXPORT_ROLE_LABELS[m.sender_role]
          ? t(EXPORT_ROLE_LABELS[m.sender_role])
          : m.sender_role || "";
        const name =
          m.sender_id && getDisplayName ? getDisplayName(m.sender_id) : "";
        return name && name !== m.sender_id ? `${role} (${name})` : role;
      };

      // Fetch secuencial del detalle de cada conversación. Un fallo puntual no
      // aborta el export: esa conversación queda sin mensajes (y sin conteo).
      const messagesByConv = new Map(); // id → messages[]|null
      for (let i = 0; i < toExport.length; i += 1) {
        const conv = toExport[i];
        try {
          if (conv.channel === "ai") {
            // Los logs de IA no viven en conversations/:id (mismo camino que
            // loadDetail): se traen del log y se mapean al shape de mensaje.
            const log = await getAILog(conv.id);
            messagesByConv.set(
              conv.id,
              (log.messages ?? []).map((m) => ({
                sender_role: m.role === "assistant" ? "ai" : m.role,
                sender_id: m.role === "user" ? log.user_id : null,
                content: m.content,
                created_at: log.created_at,
              }))
            );
          } else {
            const detail = await getConversation(conv.id, user, conv.channel);
            messagesByConv.set(conv.id, detail.messages ?? []);
          }
        } catch (err) {
          console.warn("Export: detalle no disponible:", conv.id, err);
          messagesByConv.set(conv.id, null);
        }
        setExportProgress({ current: i + 1, total: toExport.length });
      }

      // Hoja "Conversaciones": una fila por conversación del filtro actual.
      const listRows = toExport.map((c) => {
        const messages = messagesByConv.get(c.id);
        return [
          formatDate(c.created_at),
          contactOf(c),
          c.effective_commerce_name || "",
          // Sin canal = portal (misma convención que los contadores del rail).
          c.channel ? t(CHANNEL_NAME[c.channel] || c.channel) : t("Portal"),
          EXPORT_STATUS_LABELS[c.status]
            ? t(EXPORT_STATUS_LABELS[c.status])
            : c.status || "",
          c.assigned_to
            ? (getDisplayName && getDisplayName(c.assigned_to)) || c.assigned_to
            : "",
          messages ? messages.length : "",
          formatDate(c.last_message_at || c.created_at),
        ];
      });
      const wb = XLSX.utils.book_new();
      const wsList = XLSX.utils.aoa_to_sheet([
        [
          t("Fecha inicio"),
          t("Contacto"),
          t("Comercio"),
          t("Canal"),
          t("Estado"),
          t("Agente asignado"),
          t("Cantidad de mensajes"),
          t("Último mensaje"),
        ],
        ...listRows,
      ]);
      wsList["!cols"] = [
        { wch: 18 }, // Fecha inicio
        { wch: 28 }, // Contacto
        { wch: 24 }, // Comercio
        { wch: 12 }, // Canal
        { wch: 16 }, // Estado
        { wch: 24 }, // Agente asignado
        { wch: 20 }, // Cantidad de mensajes
        { wch: 18 }, // Último mensaje
      ];
      XLSX.utils.book_append_sheet(wb, wsList, t("Conversaciones"));

      // Hoja "Mensajes": todos los mensajes de las conversaciones exportadas,
      // en el mismo orden que la hoja anterior.
      const messageRows = [];
      for (const conv of toExport) {
        const messages = messagesByConv.get(conv.id);
        if (!messages) continue;
        const contact = contactOf(conv);
        for (const m of messages) {
          const typeInfo =
            EXPORT_MESSAGE_TYPES[m.content_type] || EXPORT_MESSAGE_TYPES.text;
          // Adjunto sin texto → solo el marcador; con caption van los dos.
          const text = [typeInfo.marker && t(typeInfo.marker), m.content]
            .filter(Boolean)
            .join(" ");
          messageRows.push([
            contact,
            formatDate(m.created_at),
            whoOf(m),
            t(typeInfo.label),
            text,
          ]);
        }
      }
      const wsMessages = XLSX.utils.aoa_to_sheet([
        [t("Conversación"), t("Fecha/hora"), t("Quién"), t("Tipo"), t("Texto")],
        ...messageRows,
      ]);
      wsMessages["!cols"] = [
        { wch: 28 }, // Conversación
        { wch: 18 }, // Fecha/hora
        { wch: 22 }, // Quién
        { wch: 14 }, // Tipo
        { wch: 80 }, // Texto
      ];
      XLSX.utils.book_append_sheet(wb, wsMessages, t("Mensajes"));

      XLSX.writeFile(
        wb,
        `helpdesk-conversaciones-${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch (err) {
      console.error("No se pudo exportar a Excel:", err);
    } finally {
      setExportProgress(null);
    }
  }, [visibleConversations, exportProgress, getDisplayName, user, t]);

  // ── Abrir una conversación en el detalle ──────────────────────────────────
  // El hook carga los datos y la página agrega el cambio de pantalla en móvil.
  // Corre en el mismo tick síncrono que los setState del hook (antes de que el
  // fetch resuelva), así que React los aplica en un solo render, como antes.
  const loadDetail = useCallback(
    (conv) => {
      const pending = selectConversation(conv);
      if (isMobile) setMobileView("detail");
      return pending;
    },
    [selectConversation, isMobile]
  );

  // Mismo cambio de pantalla para lo que se abre por referencia (resultado de
  // búsqueda o ticket): el hook usa la fila cargada si existe.
  const openInDetail = useCallback(
    (ref) => {
      const pending = openConversation(ref);
      if (isMobile) setMobileView("detail");
      return pending;
    },
    [openConversation, isMobile]
  );

  // "Escribir a repartidor": mismo botón y modal que /admin/helpdesk/incidencias.
  const writeToDriver = useWriteToDriver({
    refreshList: fetchList,
    openConversation: openInDetail,
  });

  // ── Deep-link ?phone= ─────────────────────────────────────────────────────
  // Abre la conversación cuyo contact_phone/external_id termina en el número
  // pedido (comparación por sufijo de 8 dígitos: tolera con/sin código de
  // país y formato visual). Si no hay conversación con ese número, se deja en
  // el buscador para que el agente vea el "sin resultados" y decida.
  useEffect(() => {
    const target = deepLinkPhoneRef.current;
    if (!target || listLoading) return;
    deepLinkPhoneRef.current = null;
    const tail = target.slice(-8);
    const match = conversations.find((c) => {
      const digits = String(c.contact_phone || c.external_id || "").replace(/\D/g, "");
      return digits.length >= tail.length && digits.slice(-8) === tail;
    });
    if (match) {
      loadDetail(match);
    } else {
      setSearchQuery(target);
    }
    // setSearchQuery es el setter de useState del hook: su identidad no cambia,
    // así que listarlo no altera cuándo corre el efecto (eslint no puede
    // inferir esa estabilidad a través del hook).
  }, [listLoading, conversations, loadDetail, setSearchQuery]);

  const handleBack = useCallback(() => {
    setMobileView("list");
    clearSelection();
  }, [clearSelection]);

  // ── Layout helpers (mobile master-detail) ─────────────────────────────────
  const showList = !isMobile || mobileView === "list";
  const showDetail = !isMobile || mobileView === "detail";
  // En mobile el patrón una-columna-a-la-vez YA es el modo foco natural: el
  // atajo y el botón quedan inertes y no hay divisores que arrastrar.
  const focusModeActive = focusMode && !isMobile;
  // La columna fija de contacto solo se abre si de verdad cabe junto a la
  // lista y al chat en sus mínimos. A 1280px con el sidebar y el rail
  // desplegados el grid mide ~810px y CHAT_MIN no se puede garantizar: antes
  // el chat quedaba aplastado a ~250px (el nombre del contacto desaparecía
  // detrás de «Tomar caso»). Sin medida todavía (primer render) se asume que
  // cabe, igual que en clampWidths. Cuando no cabe, el header del chat abre
  // el panel como overlay (misma ruta que bajo 1280px).
  const contactPanelFits =
    !containerWidth || containerWidth >= LIST_MIN + CHAT_DOCK_MIN + CONTACT_MIN;
  const canDockContactPanel = isWideDesktop && contactPanelFits;
  const showContactPanel = !!(
    canDockContactPanel &&
    contactPanelOpen &&
    selectedConv &&
    !focusModeActive
  );

  // ── Anchos efectivos ──────────────────────────────────────────────────────
  const { list: clampedListWidth, side: clampedContactWidth, maxList, maxSide: maxContact } =
    useMemo(
      () =>
        clampWidths(
          {
            containerWidth,
            listWidth,
            sideWidth: contactWidth,
            sideVisible: showContactPanel,
          },
          WIDTH_LIMITS
        ),
      [containerWidth, listWidth, contactWidth, showContactPanel]
    );
  // En modo foco la lista se dibuja al mínimo SIN tocar el ancho guardado: al
  // salir vuelve exactamente a como estaba.
  const effectiveListWidth = focusModeActive ? LIST_MIN : clampedListWidth;

  // Medida del contenedor: sin ella no se puede garantizar CHAT_MIN. El valor
  // solo cambia al redimensionar la ventana o el sidebar, no durante el
  // arrastre (el grid mide siempre lo mismo, solo reparte distinto).
  // useLayoutEffect: la medida decide si la columna de contacto cabe; con
  // useEffect el primer frame la montaba y el segundo la desmontaba.
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el || isMobile) return undefined;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      setContainerWidth((prev) => (Math.abs(prev - width) < 1 ? prev : width));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  // Escribe el ancho vivo directamente en el DOM: durante el arrastre no hay
  // setState, así que ni la lista ni el detalle se vuelven a renderizar.
  const setWidthVar = useCallback((panel, width) => {
    const el = gridRef.current;
    if (!el) return;
    const px = `${Math.round(width)}px`;
    if (panel === "contact") {
      el.style.setProperty("--hd-contact-w", px);
      // El contenido del panel de contacto se mide con su propia var: al
      // CERRARLO la columna va a 0px pero el contenido conserva su ancho para
      // no re-maquetarse mientras encoge; al ARRASTRARLO ambas van juntas.
      el.style.setProperty("--hd-contact-content-w", px);
    } else {
      el.style.setProperty("--hd-list-w", px);
    }
  }, []);

  const handleListLiveResize = useCallback(
    (width) => {
      dragRef.current = { panel: "list", width };
      setWidthVar("list", width);
    },
    [setWidthVar]
  );

  const handleContactLiveResize = useCallback(
    (width) => {
      dragRef.current = { panel: "contact", width };
      setWidthVar("contact", width);
    },
    [setWidthVar]
  );

  const handleListCommitResize = useCallback(
    (width) => {
      dragRef.current = null;
      setIsResizing(false);
      setListWidth(Math.round(width));
    },
    [setListWidth]
  );

  const handleContactCommitResize = useCallback(
    (width) => {
      dragRef.current = null;
      setIsResizing(false);
      setContactWidth(Math.round(width));
    },
    [setContactWidth]
  );

  // Si algo ajeno al arrastre re-renderiza la vista (llega un mensaje por SSE),
  // React reescribe el style del grid con el ancho persistido y la columna
  // saltaría hacia atrás a mitad del arrastre. Se reaplica el ancho vivo.
  useLayoutEffect(() => {
    const drag = dragRef.current;
    if (drag) setWidthVar(drag.panel, drag.width);
  });

  // ── Sidebar global ────────────────────────────────────────────────────────
  // El sidebar de mainLayout es estado COMPARTIDO con el resto de la app: se
  // colapsa mientras se está en el helpdesk y se restaura al desmontar la vista
  // o, si la pestaña murió antes, al salir la próxima vez (el snapshot está
  // persistido).
  const sidebar = useSidebar();
  const sidebarRef = useRef(sidebar);
  sidebarRef.current = sidebar;
  const setSidebarCollapsed = useCallback((next) => {
    const api = sidebarRef.current;
    if (!api) return;
    if (typeof api.setCollapsed === "function") {
      api.setCollapsed(next);
      return;
    }
    // Provider sin setter (versión anterior): se emula con el toggle existente.
    if (typeof api.toggleCollapsed === "function" && !!api.isCollapsed !== next) {
      api.toggleCollapsed();
    }
  }, []);

  const focusPrevRef = useRef(focusPrev);
  useEffect(() => {
    focusPrevRef.current = focusPrev;
  }, [focusPrev]);

  const restoreFocusSnapshot = useCallback(() => {
    const snapshot = focusPrevRef.current;
    if (!snapshot) return;
    focusPrevRef.current = null;
    if (typeof snapshot.sidebar === "boolean") setSidebarCollapsed(snapshot.sidebar);
    setFocusPrev(null);
  }, [setSidebarCollapsed, setFocusPrev]);

  // En escritorio el sidebar global se colapsa al ENTRAR al helpdesk (no solo en
  // modo foco): la conversación necesita el ancho. Se restaura al salir de la
  // vista con el mismo snapshot, así la preferencia del resto de la app no cambia.
  // Si la persona lo expande a mano dentro del helpdesk, se respeta hasta salir.
  useEffect(() => {
    if (isMobile) return;
    if (!focusPrevRef.current) {
      const snapshot = { sidebar: !!sidebarRef.current?.isCollapsed };
      focusPrevRef.current = snapshot;
      setFocusPrev(snapshot);
    }
    setSidebarCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Salir del helpdesk (navegar a otra ruta) también devuelve el sidebar.
  useEffect(
    () => () => restoreFocusSnapshot(),
    [restoreFocusSnapshot]
  );

  const toggleFocusMode = useCallback(() => {
    if (isMobile) return;
    setFocusMode((value) => !value);
  }, [isMobile, setFocusMode]);

  // Pedir el rail de vuelta ("[" o su botón) equivale a salir del modo foco: es
  // la única forma de que ese control no quede muerto mientras el foco lo
  // mantiene comprimido.
  const exitFocusMode = useCallback(() => setFocusMode(false), [setFocusMode]);

  // El panel de contacto se desmonta DESPUÉS de la transición de cierre: si se
  // desmontara al instante, la columna se vaciaría antes de terminar de encoger.
  const [contactPanelMounted, setContactPanelMounted] = useState(showContactPanel);
  useEffect(() => {
    if (showContactPanel) {
      setContactPanelMounted(true);
      return undefined;
    }
    const timer = setTimeout(() => setContactPanelMounted(false), PANEL_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [showContactPanel]);

  // Elegir bandeja en el rail: el hook cambia los filtros y suelta la
  // conversación abierta; en móvil, además, se vuelve a la lista.
  const handleSelectInboxKey = useCallback((key) => {
    selectInbox(key);
    if (isMobile) setMobileView("list");
  }, [selectInbox, isMobile]);

  // Elegir una cuenta del sub-árbol del rail: entra al canal y filtra por cuenta.
  const handleSelectAccount = useCallback((key, account) => {
    selectInbox(key, account);
    if (isMobile) setMobileView("list");
  }, [selectInbox, isMobile]);

  // Vista de un número retirado: el filtro lo hace el servidor (sus hilos no
  // vienen en ninguna otra bandeja), no el filtro por cuenta del cliente.
  const handleSelectRetiredAccount = useCallback((account) => {
    selectInbox(`${RETIRED_KEY_PREFIX}${account.id}`);
    if (isMobile) setMobileView("list");
  }, [selectInbox, isMobile]);

  const handleSelectFolder = useCallback((fid) => {
    selectInbox(`folder.${fid}`);
    if (isMobile) setMobileView("list");
  }, [selectInbox, isMobile]);

  // El buscador vive dentro de ConversationList; se localiza por el contenedor
  // en vez de arrastrar una ref a través de sus props.
  const focusSearch = useCallback(() => {
    if (isMobile) setMobileView("list");
    requestAnimationFrame(() => {
      const input = listPanelRef.current?.querySelector('input[type="search"]');
      input?.focus();
      input?.select?.();
    });
  }, [isMobile]);

  // ── Atajos de la vista ────────────────────────────────────────────────────
  // "]" panel de contacto · "\" modo foco · "/" buscar.
  // El rail ("[") lo maneja HelpdeskSuiteLayout, que es quien tiene ese estado.
  // La familia física queda completa: "[" rail, "]" contacto, "\" todo junto.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || hasBlockingModifier(event)) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === "]") {
        // Sin sitio para acoplar la columna, el atajo invertiría la
        // preferencia persistida sin efecto visible: se ignora.
        if (!canDockContactPanel) return;
        event.preventDefault();
        setContactPanelOpen((open) => !open);
      } else if (event.key === "\\") {
        event.preventDefault();
        toggleFocusMode();
      } else if (event.key === "/") {
        event.preventDefault();
        focusSearch();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canDockContactPanel, focusSearch, setContactPanelOpen, toggleFocusMode]);

  // Abrir la conversación de un resultado de búsqueda en mensajes. Si no está en
  // la lista cargada, basta con id + canal: loadDetail trae el resto.
  const handleSelectMessageResult = useCallback(
    (result) => {
      const convId = result?.conversation_id;
      if (!convId) return;
      const conversation = searchResultConversation(result);
      openInDetail({
        id: convId,
        channel: conversation.channel,
        contact_name: conversation.contact_name,
        external_id: conversation.external_id,
      });
    },
    [openInDetail]
  );

  // Abrir la conversación del ticket consultado. Igual que un resultado de
  // búsqueda: si la conversación no está cargada en la lista, basta con su id y
  // el canal, porque loadDetail trae el resto.
  const handleSelectTicket = useCallback(
    (ticket) => {
      const convId = ticket?.conversation_id;
      if (!convId) return;
      openInDetail({
        id: convId,
        channel: ticket.channel,
        contact_name: ticket.contact_name,
        contact_phone: ticket.contact_phone,
      });
    },
    [openInDetail],
  );

  // Tres columnas en desktop: animar grid-template-columns mueve UNA propiedad
  // del contenedor en vez del ancho de cada panel. En mobile seguimos con el
  // master-detail de siempre (una columna a la vez, sin transición de ancho).
  //
  // Los anchos laterales viajan como CSS vars: durante el arrastre el divisor
  // las reescribe en el DOM sin pasar por React (ver setWidthVar).
  const gridStyle = isMobile
    ? undefined
    : {
        "--hd-list-w": `${effectiveListWidth}px`,
        "--hd-contact-w": showContactPanel ? `${clampedContactWidth}px` : "0px",
        "--hd-contact-content-w": `${clampedContactWidth}px`,
        gridTemplateColumns: "var(--hd-list-w) minmax(0,1fr) var(--hd-contact-w)",
      };

  return (
    <HelpdeskSuiteLayout
      activeKey={inboxKey}
      counters={counters}
      onSelectInboxKey={handleSelectInboxKey}
      channelAccounts={channelAccounts}
      accountFilter={accountFilter}
      onSelectAccount={handleSelectAccount}
      onSelectRetiredAccount={handleSelectRetiredAccount}
      onAccountsChanged={handleAccountsChanged}
      folders={folders}
      onSelectFolder={handleSelectFolder}
      onCreateFolder={handleCreateFolder}
      onRenameFolder={handleRenameFolder}
      onDeleteFolder={handleDeleteFolder}
      forceRailCollapsed={focusModeActive}
      onReleaseRailForce={exitFocusMode}
    >
      <div
        ref={gridRef}
        style={gridStyle}
        className={cn(
          "w-full h-full min-h-0",
          isMobile
            ? "flex"
            : "grid grid-rows-1 transition-[grid-template-columns] duration-200 ease-out motion-reduce:transition-none",
          // Durante el arrastre la transición de 200ms pelearía con cada frame
          // del puntero (rubber-banding), y select-none evita ir seleccionando
          // el texto del chat mientras se arrastra.
          isResizing && "transition-none select-none",
        )}
      >
        {/* Panel izquierdo — lista */}
        {showList && (
          <aside
            ref={listPanelRef}
            id="helpdesk-list-panel"
            className={cn(
              "flex flex-col min-w-0 bg-bg",
              isMobile ? "w-full" : "overflow-hidden border-r border-border",
            )}
          >
            {retiredAccountId && retiredAccount && (
              <RetiredAccountBanner
                account={retiredAccount}
                openCount={visibleConversations.length}
                onChanged={handleAccountsChanged}
              />
            )}
            <ConversationList
              conversations={visibleConversations}
              total={visibleConversations.length}
              loading={listLoading}
              error={listError}
              onRetry={fetchList}
              inboxKey={inboxKey}
              titleOverride={
                retiredAccountId
                  ? retiredAccount?.phone
                    ? t("Número retirado {{numero}}").replace(
                        "{{numero}}",
                        formatPhone(retiredAccount.phone)
                      )
                    : t("Número retirado")
                  : undefined
              }
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              commerceFilter={commerceFilter}
              onCommerceFilterChange={setCommerceFilter}
              labelFilter={labelFilter}
              onLabelFilterChange={setLabelFilter}
              selectedId={selectedConv?.id}
              onSelect={loadDetail}
              newIds={newIds}
              userNames={userNames}
              getDisplayName={getDisplayName}
              showAllNumbers={showAllNumbers}
              onToggleAllNumbers={() => setShowAllNumbers((v) => !v)}
              foldersById={foldersById}
              activeFolderId={activeFolderId}
              onExport={handleExportExcel}
              onWriteToDriver={writeToDriver.open}
              exportDisabled={visibleConversations.length === 0}
              exportProgress={exportProgress}
              messageSearchAlsoEmpty={bothSearchesEmpty}
              aiLogsError={aiLogsError}
              pins={pinsAvailable ? pins : undefined}
              onTogglePin={pinsAvailable ? togglePin : undefined}
            />

            {/* Ticket consultado: resultado exacto, arriba de todo lo demás. */}
            {ticketLookupVisible && (
              <TicketLookupResult
                number={ticketQueryNumber}
                loading={!ticketLookupFresh || ticketLookupLoading}
                ticket={ticketLookupFresh ? ticketResult : null}
                error={ticketLookupFresh ? ticketLookupError : null}
                onSelect={handleSelectTicket}
              />
            )}

            {/* Búsqueda global: coincidencias dentro de los mensajes, debajo de
                los resultados locales. Si el endpoint no existe, no se renderiza. */}
            {showMessageSearch && !bothSearchesEmpty && (
              <MessageSearchResults
                query={trimmedQuery}
                loading={messageSearchLoading}
                results={visibleMessageResults}
                error={messageSearchError}
                onSelect={handleSelectMessageResult}
              />
            )}
          </aside>
        )}

        {/* Panel central — detalle */}
        {showDetail && (
          <main
            className={cn(
              "relative flex flex-col min-w-0 bg-bg",
              isMobile ? "flex-1" : "overflow-hidden",
            )}
          >
            {/* Divisores: viven DENTRO del panel central porque el aside de la
                lista tiene overflow-hidden y ahí se recortarían. En modo foco no
                se renderizan: no hay nada que repartir con la lista al mínimo. */}
            {!isMobile && !focusModeActive && (
              <PanelResizer
                edge="left"
                label={t("Ajustar ancho de la lista de conversaciones")}
                controlsId="helpdesk-list-panel"
                value={clampedListWidth}
                min={LIST_MIN}
                max={maxList}
                defaultValue={LIST_DEFAULT}
                step={RESIZE_STEP}
                onResizingChange={setIsResizing}
                onChange={handleListLiveResize}
                onCommit={handleListCommitResize}
              />
            )}
            {!isMobile && showContactPanel && (
              <PanelResizer
                edge="right"
                label={t("Ajustar ancho del panel de contacto")}
                controlsId="helpdesk-contact-panel"
                value={clampedContactWidth}
                min={CONTACT_MIN}
                max={maxContact}
                defaultValue={CONTACT_DEFAULT}
                step={RESIZE_STEP}
                onResizingChange={setIsResizing}
                onChange={handleContactLiveResize}
                onCommit={handleContactCommitResize}
              />
            )}

            {selectedConv ? (
              <ConversationDetail
                conversation={selectedConv}
                channelAccount={selectedChannelAccount}
                messages={detailMessages}
                loading={detailLoading}
                onUpdated={handleConvUpdated}
                onTakeover={handleAiTakeover}
                onBack={isMobile ? handleBack : null}
                userNames={userNames}
                getDisplayName={getDisplayName}
                onToggleContactPanel={
                  canDockContactPanel ? () => setContactPanelOpen((p) => !p) : null
                }
                contactPanelOpen={contactPanelOpen}
                onToggleFocusMode={isMobile ? null : toggleFocusMode}
                focusMode={focusModeActive}
                folders={folders}
                foldersById={foldersById}
                onMoveToFolder={handleMoveToFolder}
                detailError={detailError}
                onRetryDetail={
                  selectedConv ? () => loadDetail(selectedConv) : undefined
                }
                onRefreshConversation={reloadConversation}
                pinned={pins?.has(selectedConv.id) ?? false}
                onTogglePin={
                  pinsAvailable && isPinnable(selectedConv) ? togglePin : undefined
                }
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-[24px] py-[48px] bg-bg-muted/50">
                {isMobile && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="absolute left-4 top-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
                  >
                    <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                    {t("Volver")}
                  </button>
                )}
                {/* Calm empty state: neutral tile, the accent only as a thin
                    ring detail, quiet copy. */}
                <div className="mb-[16px] w-[56px] h-[56px] rounded-2xl bg-bg border border-border/60 shadow-soft text-fg-muted flex items-center justify-center">
                  <MessagesSquare className="w-[26px] h-[26px]" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <p className="text-[15px] leading-[22px] font-semibold text-fg">
                  {t("Sin conversación seleccionada")}
                </p>
                <p className="mt-[4px] max-w-[34ch] text-[13px] leading-[20px] text-fg-muted">
                  {t("Selecciona una conversación de la lista para ver el detalle.")}
                </p>
                {/* Solo si el operador aún no vinculó ningún WhatsApp (con las
                    cuentas desconocidas —null— no se insiste). */}
                {Array.isArray(channelAccounts) &&
                  !channelAccounts.some(
                    (a) => a?.channel === "whatsapp" || a?.channel === "whatsapp_unofficial"
                  ) && <LinkWhatsAppHint />}
              </div>
            )}
          </main>
        )}

        {/* Panel derecho — contacto (solo desktop ≥1280px y con conversación
            seleccionada). La columna encoge a 0px al cerrarlo; el contenido
            mantiene su ancho para que no se re-maquete mientras se anima. */}
        {!isMobile && canDockContactPanel && contactPanelMounted && selectedConv && (
          <aside
            id="helpdesk-contact-panel"
            aria-label={t("Panel de contacto")}
            aria-hidden={!showContactPanel}
            className={cn(
              "min-w-0 overflow-hidden bg-bg flex flex-col",
              showContactPanel && "border-l border-border",
            )}
          >
            <div
              style={{ width: "var(--hd-contact-content-w)" }}
              className="h-full flex flex-col overflow-hidden"
            >
              <ContactPanel
                conversation={selectedConv}
                onConversationUpdate={handleConvUpdated}
                mode="fixed"
                // El cierre vive DENTRO del panel (X en su header): colapsa la
                // columna igual que el click en el bloque de contacto del chat.
                onClose={() => setContactPanelOpen(false)}
              />
            </div>
          </aside>
        )}
      </div>
      {writeToDriver.modal}
    </HelpdeskSuiteLayout>
  );
}
